// Best-effort sessionStorage cache of the current search result set, used by the map to rehydrate
// after a page reload (see Map/googleMaps.tsx — it reads dataContext.mapData first and only falls
// back to this key). Losing the cache costs one extra search; letting it throw costs the booking.
//
// Why this needs to be defensive: Web Storage is capped per origin and browsers count UTF-16, so a
// 2.5 M character result set occupies ~5 MB — right at Safari's limit. A desktop search sends
// mobile=false, which lifts api3's 100-vehicle cap, so a large city can return 400+ vehicles and
// overflow it. The write used to run *before* setMapData in changeMapData, so QuotaExceededError
// aborted the search: the caller's catch reported "error fetching results" and, with mapData left
// empty, the filter produced an empty list — a search that succeeded rendered as "no cars".
import { IVehicleSearch } from "../interfaces/VehicleSearch";

const STORAGE_KEY = "allCars";

// Storage quota is not recoverable within a session, so once a write fails stop paying for the
// stringify on every subsequent search.
let cacheUnavailable = false;

/** Drop the cached result set. Safe to call when storage is unavailable. */
export const clearMapDataCache = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage disabled entirely — there is nothing to clean up */
  }
};

/**
 * Cache `data` for map rehydration. Never throws.
 * Returns true when the data was stored, false when it was skipped or dropped.
 */
export const cacheMapData = (data: IVehicleSearch[]): boolean => {
  if (data.length === 0 || cacheUnavailable) {
    // Always clear rather than leave a previous search's vehicles behind for the map to find.
    clearMapDataCache();
    return false;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch {
    // QuotaExceededError, or storage blocked (Safari private browsing, disabled cookies).
    cacheUnavailable = true;
    clearMapDataCache();
    return false;
  }
};
